import React, { useState, useMemo, useEffect, useRef } from "react";
import { getWorkers, getCalendarAppointments, getCitasCalendarioPorSemana, getAusenciasByFechas } from "../../services/apiService";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "moment/locale/es";
import "react-big-calendar/lib/css/react-big-calendar.css";
import styles from "./calendar.module.css";
import { MyIcons } from "../../components/icons/my-icons";
import Checkbox from "../../components/ui/checkbox";
import Input from "../../components/ui/input";
import PageTitle from "../../components/screens/page-title";
import { useNavigate } from "react-router-dom";
import usePageTitle from "../../hooks/usePageTitle";
import AppointmentDetailModal from "../../components/modals/appointment-detail-modal";
import AbsencesModal from "../../components/modals/absences-modal";
// Configuración del localizador para español
moment.locale("es");
const localizer = momentLocalizer(moment);

const CalendarPage = () => {
  usePageTitle('Calendario');
  const navigate = useNavigate();
  const userRole = localStorage.getItem("userRole") || "";
  const [operarios, setOperarios] = useState([]);
  const [searchOperario, setSearchOperario] = useState("");
  const [selectedOperarios, setSelectedOperarios] = useState(() => {
    // Restaurar operarios seleccionados desde localStorage
    const saved = localStorage.getItem('calendarSelectedOperarios');
    return saved ? JSON.parse(saved) : [];
  });
  const [currentDate, setCurrentDate] = useState(new Date());
  const [citas, setCitas] = useState([]);
  const [ausencias, setAusencias] = useState([]);
  const [calendarView, setCalendarView] = useState('week');
  const [, setLoading] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isAbsencesModalOpen, setIsAbsencesModalOpen] = useState(false);
  const [useCompactView, setUseCompactView] = useState(() => {
    const saved = localStorage.getItem('calendarViewMode');
    return saved ? saved === 'compact' : true; // Por defecto la nueva vista
  });
  const calendarContainerRef = useRef(null);
  const filterDropdownRef = useRef(null);
  
  // Determinar si el usuario es OPERARIO
  const isOperario = userRole === "ROLE_OPERARIO";
  
  // Estados para el modal de citas (solo operarios)
  const [selectedCita, setSelectedCita] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Estado para filtro de tipo de citas (para operarios)
  const [citaFilter, setCitaFilter] = useState("todas"); // "todas", "pendientes", "proximas", "historial"

  const handleOperarioToggle = (operarioId) => {
    setSelectedOperarios((prevSelected) => {
      const newSelected = prevSelected.includes(operarioId)
        ? prevSelected.filter((id) => id !== operarioId)
        : [...prevSelected, operarioId];
      
      // Guardar en localStorage
      localStorage.setItem('calendarSelectedOperarios', JSON.stringify(newSelected));
      return newSelected;
    });
  };

  const handleClearFilters = () => {
    setSelectedOperarios([]);
    setSearchOperario("");
    // Limpiar localStorage
    localStorage.removeItem('calendarSelectedOperarios');
  };
  
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCita(null);
  };
  
  const handleModalUpdate = () => {
    // Recargar las citas después de actualizar/cerrar una cita
    const fetchCitas = async () => {
      setLoading(true);
      try {
        let fromDate;
        let toDate;
        if (calendarView === 'week') {
          fromDate = moment(currentDate).startOf('week').format('YYYY-MM-DD');
          toDate = moment(currentDate).endOf('week').format('YYYY-MM-DD');
        } else if (calendarView === 'day') {
          fromDate = moment(currentDate).startOf('day').format('YYYY-MM-DD');
          toDate = moment(currentDate).endOf('day').format('YYYY-MM-DD');
        } else {
          fromDate = moment(currentDate).startOf('month').format('YYYY-MM-DD');
          toDate = moment(currentDate).endOf('month').format('YYYY-MM-DD');
        }
        
        let allCitas = [];
        try {
          const citasPorSemana = await getCitasCalendarioPorSemana({ fromDate, toDate });
          allCitas = citasPorSemana.map((cita) => {
            const startDate = new Date(cita.fechaCita);
            const endDate = new Date(cita.fechaCitaFin);
            
            return {
              id: cita.citaId,
              citaId: cita.citaId,
              expedienteId: cita.expedienteId,
              titulo: null,
              title: cita.expedienteId ? `Exp: ${cita.expedienteId}` : "",
              start: startDate,
              end: endDate,
              allDay: false,
              domicilioCliente: cita.domicilioCliente,
              localidad: cita.localidadCliente,
              estadoCita: cita.estadoCita,
              operarioNombre: "Mis citas",
              operarioId: null,
              resource: null,
            };
          });
        } catch (error) {
          if (selectedOperarios.length > 0) {
            const citas = await getCalendarAppointments({
              operarioIds: selectedOperarios,
              fromDate,
              toDate,
            });
            allCitas = citas;
          } else {
            const citasAll = await getCalendarAppointments({
              operarioIds: [],
              fromDate,
              toDate,
            });
            allCitas = citasAll;
          }
          
          allCitas = allCitas.map((cita) => ({
            ...cita,
            titulo: cita.titulo || null,
            title: cita.titulo ? cita.titulo : (cita.expedienteId ? `Exp: ${cita.expedienteId}` : ""),
            start: new Date(cita.start),
            end: new Date(cita.end),
            resource: cita.operarioId,
          }));
        }
        
        setCitas(allCitas);
      } catch (error) {
        setCitas([]);
      } finally {
        setLoading(false);
      }
    };
    fetchCitas();
  };

  const toggleViewMode = () => {
    const newMode = !useCompactView;
    setUseCompactView(newMode);
    localStorage.setItem('calendarViewMode', newMode ? 'compact' : 'classic');
    if (!newMode) {
      setIsFilterOpen(false); // Cerrar dropdown al cambiar a vista clásica
    }
  };

  const handleNavigate = (date) => {
    setCurrentDate(date);
  };

  const handleSelectEvent = (event, e) => {
    if (!event.expedienteId) return;
    
    // Si es OPERARIO, abrir modal en lugar de navegar
    if (isOperario) {
      try {
        const isMiddle = !!(e && (e.nativeEvent?.button === 1 || e.button === 1 || e.which === 2));
        const isModifier = !!(e && (e.ctrlKey || e.metaKey));
        
        // Si es click medio o con modificador, abrir expediente en nueva pestaña
        if (isMiddle || isModifier) {
          window.open(`/record/${event.expedienteId}`, "_blank");
        } else {
          // Click normal: abrir modal de la cita
          setSelectedCita(event);
          setIsModalOpen(true);
        }
      } catch (err) {
        // Fallback: abrir modal
        setSelectedCita(event);
        setIsModalOpen(true);
      }
      return;
    }
    
    // Para otros roles: comportamiento normal (navegar al expediente)
    try {
      const isMiddle = !!(e && (e.nativeEvent?.button === 1 || e.button === 1 || e.which === 2));
      const isModifier = !!(e && (e.ctrlKey || e.metaKey));
      const url = `/record/${event.expedienteId}`;
      if (isMiddle || isModifier) {
        window.open(url, "_blank");
      } else {
        navigate(url);
      }
    } catch (err) {
      navigate(`/record/${event.expedienteId}`);
    }
  };

  const EventComponent = ({ event }) => {
    // Si es una ausencia, mostrar diferente
    if (event.isAusencia) {
      return (
        <div
          className={`${styles.eventContent} ${styles.ausenciaEvent}`}
          title={`AUSENCIA: ${event.operarioNombre}\nMotivo: ${event.motivo || 'No especificado'}\nFechas: ${moment(event.start).format('DD/MM/YYYY')} - ${moment(event.end).format('DD/MM/YYYY')}${event.observaciones ? '\nObservaciones: ' + event.observaciones : ''}`}
        >
          <div className={styles.eventHeader}>
            <div className={styles.eventTitle}>🚫 AUSENCIA</div>
          </div>
          <div className={styles.eventDetails}>
            <div className={styles.ausenciaOperario}>{event.operarioNombre}</div>
            {event.motivo && (
              <div className={styles.ausenciaMotivo}>{event.motivo}</div>
            )}
          </div>
        </div>
      );
    }
    
    // Detectar si es una cita "sin hora" (00:00 - 01:00)
    const startMoment = moment(event.start);
    const endMoment = moment(event.end);
    const isSinHora = startMoment.hour() === 0 && startMoment.minute() === 0 &&
                     endMoment.hour() === 1 && endMoment.minute() === 0;
    
    const timeDisplay = isSinHora 
      ? "Sin hora" 
      : `${startMoment.format("HH:mm")} - ${endMoment.format("HH:mm")}`;
    
    return (
      <div
        className={styles.eventContent}
        data-expediente={event.expedienteId}
        title={`Título: ${event.titulo || event.expedienteId}\nOperario: ${
          event.operarioNombre
        }\nDirección: ${event.domicilioCliente}\nLocalidad: ${event.localidad || ''}\nHora: ${timeDisplay}`}
        onAuxClick={(e) => {
          // middle click
          e.preventDefault();
          e.stopPropagation();
          handleSelectEvent(event, e);
        }}
        onClick={(e) => {
          // Ctrl/Cmd + click to open in new tab
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            e.stopPropagation();
            handleSelectEvent(event, e);
          }
        }}
      >
        <div className={styles.eventHeader}>
          <div className={styles.eventTitle}>{event.titulo || event.expedienteId}</div>
          <div className={styles.eventTime}>{timeDisplay}</div>
        </div>
        <div className={styles.eventDetails}>
          <div className={styles.eventAddress}>{event.domicilioCliente}</div>
          {event.localidad && (
            <div className={styles.eventLocality}>{event.localidad}</div>
          )}
        </div>
      </div>
    );
  };

  const CustomToolbar = (toolbar) => {
    const dateInputRef = useRef(null);

    const goToToday = () => toolbar.onNavigate("TODAY");
    const goToPrev = () => toolbar.onNavigate("PREV");
    const goToNext = () => toolbar.onNavigate("NEXT");

    const handleLabelClick = () => {
      if (dateInputRef.current) {
        dateInputRef.current.showPicker
          ? dateInputRef.current.showPicker() // Para navegadores modernos
          : dateInputRef.current.click();     // Fallback
      }
    };

    const handleDateChange = (e) => {
      const value = e.target.value;
      if (value) {
        const [year, month, day] = value.split("-");
        const newDate = new Date(year, month - 1, day);
        toolbar.onNavigate("DATE", newDate);
      }
    };

    const date = moment(toolbar.date);

    return (
      <div className={styles.customToolbar}>
        <div className={styles.toolbarControls}>
          <button onClick={goToPrev} className={styles.toolbarButton}>
            <MyIcons
              name={"arrowLeft"}
              width={20}
              height={20}
              color={"var(--main)"}
            />
          </button>
          
          <span
            className={styles.toolbarLabel}
            onClick={handleLabelClick}
            title="Elegir mes y año"
          >
            {date.format("MMMM YYYY")}
          </span>
          
          <input
            ref={dateInputRef}
            type="date"
            style={{ opacity: 0, position: "absolute", zIndex: -1 }}
            value={date.format("YYYY-MM-DD")}
            onChange={handleDateChange}
          />
          
          <button onClick={goToNext} className={styles.toolbarButton}>
            <MyIcons
              name={"arrowRight"}
              width={20}
              height={20}
              color={"var(--main)"}
            />
          </button>
          
          <button onClick={goToToday} className={styles.todayButton}>
            Hoy
          </button>
        </div>
      </div>
    );
  };

  const NoEventsOverlay = () => (
    <div className={styles.noEvents}>No hay citas programadas</div>
  );

  // Cargar operarios al montar
  useEffect(() => {
    const fetchOperarios = async () => {
      try {
        const data = await getWorkers();
        console.log("Operarios obtenidos:", data);
        // Filtrar solo operarios activos
        const activeOperarios = data.filter(operario => operario.inactivo === false);
        setOperarios(activeOperarios);
        // Los operarios seleccionados ya están inicializados desde localStorage
      } catch (error) {
        setOperarios([]);
      }
    };
    fetchOperarios();
  }, []);

  const filteredOperarios = useMemo(() => {
    if (!searchOperario || !searchOperario.trim()) return operarios;
    const q = searchOperario.toLowerCase();
    return operarios.filter((o) => (o.nombre || "").toLowerCase().includes(q));
  }, [operarios, searchOperario]);

  // Cargar citas y ausencias cuando cambian operarios o fechas
  useEffect(() => {
    const fetchCitas = async () => {
      setLoading(true);
      try {
        // Define el rango de fechas según la vista del calendario.
        // - week: semana completa que contiene currentDate
        // - day: el propio día
        // Por defecto (u otras vistas) usamos el mes completo.
        let fromDate;
        let toDate;
        if (calendarView === 'week') {
          fromDate = moment(currentDate).startOf('week').format('YYYY-MM-DD');
          toDate = moment(currentDate).endOf('week').format('YYYY-MM-DD');
        } else if (calendarView === 'day') {
          fromDate = moment(currentDate).startOf('day').format('YYYY-MM-DD');
          toDate = moment(currentDate).endOf('day').format('YYYY-MM-DD');
        } else {
          fromDate = moment(currentDate).startOf('month').format('YYYY-MM-DD');
          toDate = moment(currentDate).endOf('month').format('YYYY-MM-DD');
        }
        console.log("Rango de fechas:", fromDate, toDate);
        
        // Cargar ausencias en paralelo (solo para no-operarios)
        if (!isOperario) {
          try {
            const ausenciasData = await getAusenciasByFechas(fromDate, toDate);
            setAusencias(ausenciasData || []);
          } catch (error) {
            console.error("Error loading ausencias:", error);
            setAusencias([]);
          }
        }
        
        // Intentar obtener citas usando la API de operario primero
        let allCitas = [];
        try {
          const citasPorSemana = await getCitasCalendarioPorSemana({ fromDate, toDate });
          console.log('Citas calendario por semana (operario):', citasPorSemana);
          
          // Mapear los datos de la API de operario al formato del calendario
          allCitas = citasPorSemana.map((cita) => {
            // Crear objetos Date para las fechas
            const startDate = new Date(cita.fechaCita);
            const endDate = new Date(cita.fechaCitaFin);
            
            return {
              id: cita.citaId,
              citaId: cita.citaId,
              expedienteId: cita.expedienteId,
              titulo: null,
              title: cita.expedienteId ? `Exp: ${cita.expedienteId}` : "",
              start: startDate,
              end: endDate,
              allDay: false, // Nunca marcar como allDay
              domicilioCliente: cita.domicilioCliente,
              localidad: cita.localidadCliente,
              estadoCita: cita.estadoCita,
              operarioNombre: "Mis citas",
              operarioId: null,
              resource: null,
            };
          });
        } catch (error) {
          console.log('API de operario no disponible, usando API estándar');
          
          // Si falla la API de operario, usar la API estándar
          if (selectedOperarios.length > 0) {
            const citas = await getCalendarAppointments({
              operarioIds: selectedOperarios,
              fromDate,
              toDate,
            });
            allCitas = citas;
          } else {
            const citasAll = await getCalendarAppointments({
              operarioIds: [],
              fromDate,
              toDate,
            });
            allCitas = citasAll;
          }
          
          // Mapear datos de la API estándar
          allCitas = allCitas.map((cita) => ({
            ...cita,
            titulo: cita.titulo || null,
            title: cita.titulo ? cita.titulo : (cita.expedienteId ? `Exp: ${cita.expedienteId}` : ""),
            start: new Date(cita.start),
            end: new Date(cita.end),
            resource: cita.operarioId,
          }));
        }
        
        setCitas(allCitas);
      } catch (error) {
        setCitas([]);
      } finally {
        setLoading(false);
      }
    };
    fetchCitas();
  }, [selectedOperarios, currentDate, calendarView]);
  
  // Combinar citas con ausencias para mostrar en el calendario
  const allCalendarEvents = useMemo(() => {
    // Mapear ausencias a formato de eventos del calendario
    const ausenciasEvents = ausencias.map((ausencia) => {
      const operario = operarios.find((o) => o.id === ausencia.operarioId);
      const operarioNombre = operario ? `${operario.nombre} ${operario.apellidos || ""}`.trim() : "Operario";
      
      // Crear fechas: las ausencias son todo el día
      const fechaInicio = new Date(ausencia.fechaInicio);
      const fechaFin = new Date(ausencia.fechaFin);
      // Ajustar para que sea todo el día
      fechaFin.setHours(23, 59, 59, 999);
      
      return {
        id: `ausencia-${ausencia.id}`,
        ausenciaId: ausencia.id,
        expedienteId: null,
        titulo: `🚫 AUSENCIA: ${operarioNombre}`,
        title: `🚫 AUSENCIA: ${operarioNombre}`,
        start: fechaInicio,
        end: fechaFin,
        allDay: true,
        operarioNombre,
        operarioId: ausencia.operarioId,
        resource: ausencia.operarioId,
        isAusencia: true,
        motivo: ausencia.motivo,
        observaciones: ausencia.observaciones,
      };
    });
    
    // Combinar citas y ausencias
    return [...citas, ...ausenciasEvents];
  }, [citas, ausencias, operarios]);
  
  // Filtrar eventos según el filtro seleccionado (solo para operarios)
  const filteredCitas = useMemo(() => {
    if (!isOperario || citaFilter === "todas") {
      return allCalendarEvents;
    }
    
    const now = new Date();
    
    return allCalendarEvents.filter(evento => {
      // No filtrar ausencias
      if (evento.isAusencia) return true;
      
      const fechaFin = new Date(evento.end);
      const estadoCita = (evento.estadoCita || "").toUpperCase();
      
      switch (citaFilter) {
        case "pendientes":
          // Citas que no están cerradas y su fecha fin ya pasó
          return estadoCita !== "CERRADA" && estadoCita !== "CERRADO" && fechaFin < now;
        case "proximas":
          // Citas que no están cerradas y su fecha fin es futura
          return estadoCita !== "CERRADA" && estadoCita !== "CERRADO" && fechaFin >= now;
        case "historial":
          // Citas cerradas
          return estadoCita === "CERRADA" || estadoCita === "CERRADO";
        default:
          return true;
      }
    });
  }, [allCalendarEvents, citaFilter, isOperario]);
  
  // Estado para modal de ausencias del rango actual
  const [showAusenciasInfoModal, setShowAusenciasInfoModal] = useState(false);
  
  // Obtener ausencias activas en el rango visible
  const ausenciasEnRango = useMemo(() => {
    if (!ausencias || ausencias.length === 0) return [];
    
    let rangeStart, rangeEnd;
    if (calendarView === 'week') {
      rangeStart = moment(currentDate).startOf('week');
      rangeEnd = moment(currentDate).endOf('week');
    } else if (calendarView === 'day') {
      rangeStart = moment(currentDate).startOf('day');
      rangeEnd = moment(currentDate).endOf('day');
    } else {
      rangeStart = moment(currentDate).startOf('month');
      rangeEnd = moment(currentDate).endOf('month');
    }
    
    return ausencias.filter(ausencia => {
      const ausenciaStart = moment(ausencia.fechaInicio);
      const ausenciaEnd = moment(ausencia.fechaFin);
      return ausenciaStart.isSameOrBefore(rangeEnd) && ausenciaEnd.isSameOrAfter(rangeStart);
    });
  }, [ausencias, currentDate, calendarView]);
  
  // Obtener operarios únicos ausentes
  const operariosAusentes = useMemo(() => {
    const operarioIds = new Set();
    ausenciasEnRango.forEach(ausencia => operarioIds.add(ausencia.operarioId));
    return Array.from(operarioIds).map(id => {
      const operario = operarios.find(o => o.id === id);
      const ausenciasOperario = ausenciasEnRango.filter(a => a.operarioId === id);
      return {
        id,
        nombre: operario ? `${operario.nombre} ${operario.apellidos || ''}`.trim() : 'Operario',
        ausencias: ausenciasOperario
      };
    });
  }, [ausenciasEnRango, operarios]);
  

  // Global handlers: capture auxclick (middle click) and Ctrl/Cmd+click on calendar events
  useEffect(() => {
    const container = calendarContainerRef.current;
    if (!container) return;
    const handleAux = (e) => {
      // button === 1 is middle click
      try {
        if (e.button !== 1) return;
        const el = e.target.closest('[data-expediente]');
        if (el) {
          const expedienteId = el.getAttribute('data-expediente');
          if (expedienteId) {
            e.preventDefault();
            e.stopPropagation();
            window.open(`/record/${expedienteId}`, '_blank');
          }
        }
      } catch (err) {
        // ignore
      }
    };

    const handleClickWithModifier = (e) => {
      try {
        if (!(e.ctrlKey || e.metaKey)) return;
        const el = e.target.closest('[data-expediente]');
        if (el) {
          const expedienteId = el.getAttribute('data-expediente');
          if (expedienteId) {
            e.preventDefault();
            e.stopPropagation();
            window.open(`/record/${expedienteId}`, '_blank');
          }
        }
      } catch (err) {
        // ignore
      }
    };

    // Prevent autoscroll/start-of-autoscroll on mousedown middle button so auxclick can be handled
    const handleMouseDown = (e) => {
      try {
        if (e.button !== 1) return;
        const el = e.target.closest('[data-expediente]');
        if (el) {
          e.preventDefault();
          e.stopPropagation();
        }
      } catch (err) {
        // ignore
      }
    };

    // Attach listeners on the calendar container in capture phase so we can
    // intercept native middle-click behavior before the browser autoscrolls.
    // Use passive: false to allow preventDefault.
    const opts = { capture: true, passive: false };
    container.addEventListener('auxclick', handleAux, opts);
    container.addEventListener('click', handleClickWithModifier, opts);
    container.addEventListener('mousedown', handleMouseDown, opts);

    return () => {
      container.removeEventListener('auxclick', handleAux, opts);
      container.removeEventListener('click', handleClickWithModifier, opts);
      container.removeEventListener('mousedown', handleMouseDown, opts);
    };
  }, [citas]);

  // Efecto para cerrar el dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(e.target)) {
        setIsFilterOpen(false);
      }
    };
    
    if (isFilterOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isFilterOpen]);

  return (
    <div>
      <PageTitle iconName="calendarFilled" title="Calendario" />
      
      {/* Filtros de tipo de citas - Solo para OPERARIOS */}
      {isOperario && (
        <div className={styles.citaFiltersContainer}>
          <button 
            className={`${styles.filterChip} ${citaFilter === "todas" ? styles.filterChipActive : ""}`}
            onClick={() => setCitaFilter("todas")}
          >
            Todas
          </button>
          <button 
            className={`${styles.filterChip} ${citaFilter === "pendientes" ? styles.filterChipActive : ""}`}
            onClick={() => setCitaFilter("pendientes")}
          >
            Pendientes
          </button>
          <button 
            className={`${styles.filterChip} ${citaFilter === "proximas" ? styles.filterChipActive : ""}`}
            onClick={() => setCitaFilter("proximas")}
          >
            Próximas
          </button>
          <button 
            className={`${styles.filterChip} ${citaFilter === "historial" ? styles.filterChipActive : ""}`}
            onClick={() => setCitaFilter("historial")}
          >
            Historial
          </button>
        </div>
      )}
      
      <div className={`${styles.calendarContainer} ${useCompactView ? styles.compactView : styles.classicView}`} ref={calendarContainerRef}>
        {/* Botones de acción - Solo visible para no-OPERARIOS */}
        {!isOperario && (
          <div className={styles.viewToggleContainer}>
            <button 
              className={styles.absencesButton}
              onClick={() => setIsAbsencesModalOpen(true)}
              title="Gestionar ausencias de operarios"
            >
              <MyIcons name="calendar" width={20} height={20} />
              AUSENCIAS
            </button>
            <button 
              className={styles.viewToggleButton}
              onClick={toggleViewMode}
              title={useCompactView ? "Cambiar a vista clásica" : "Cambiar a vista compacta"}
            >
              <MyIcons name={useCompactView ? "menu" : "close"} width={20} height={20} />
              {useCompactView ? "Vista clásica" : "Vista compacta"}
            </button>
          </div>
        )}

        {useCompactView ? (
          // Nueva vista con dropdown
          <>
            {/* Botón de filtros - Solo visible para no-OPERARIOS */}
            {!isOperario && (
              <div className={styles.filterButtonContainer}>
                <button 
                  className={styles.filterButton}
                  onClick={() => setIsFilterOpen(!isFilterOpen)}
                >
                  <MyIcons name="filter" width={20} height={20} />
                  Filtrar operarios
                  {selectedOperarios.length > 0 && (
                    <span className={styles.filterCount}>({selectedOperarios.length})</span>
                  )}
                </button>
              </div>
            )}

            {/* Dropdown de filtros - Solo para no-OPERARIOS */}
            {!isOperario && isFilterOpen && (
              <div className={styles.filterDropdown} ref={filterDropdownRef}>
            <div className={styles.filterHeader}>
              <h2 className={styles.sidebarTitle}>Operarios</h2>
              <button 
                className={styles.closeButton}
                onClick={() => setIsFilterOpen(false)}
              >
                <MyIcons name="close" width={20} height={20} />
              </button>
            </div>
            <div className={styles.operariosList}>
              <Input
                placeholder="Buscar operario..."
                value={searchOperario}
                onChange={(e) => setSearchOperario(e.target.value)}
                style={{ marginBottom: "12px" }}
              />
              {selectedOperarios.length > 0 && (
                <button 
                  className={styles.clearFiltersButton}
                  onClick={handleClearFilters}
                >
                  <MyIcons name="close" width={16} height={16} />
                  Limpiar filtros
                </button>
              )}
              {filteredOperarios.map((operario) => {
                const operarioIndex = operarios.findIndex(
                  (op) => op.id === operario.id
                );
                const colors = [
                  "#4F46E5", // Indigo
                  "#10B981", // Emerald
                  "#F97316", // Orange
                  "#8B5CF6", // Violet
                  "#EC4899", // Pink
                  "#06B6D4", // Cyan
                  "#84CC16", // Lime
                  "#EF4444", // Red
                  "#F59E0B", // Amber
                  "#8B5A2B", // Brown
                  "#1E293B", // Slate
                  "#0EA5E9", // Sky
                  "#7C3AED", // Deep Violet
                  "#059669", // Deep Green
                  "#BE185D", // Deep Pink
                  "#DC2626", // Deep Red
                  "#EA580C", // Deep Orange
                  "#0F766E", // Teal
                  "#3B82F6", // Blue
                  "#A21CAF", // Purple
                  "#52525B", // Zinc
                  "#334155", // Blue Gray
                  "#B91C1C", // Crimson
                  "#2563EB", // Royal Blue
                  "#047857", // Forest Green
                ];
                const color = colors[operarioIndex % colors.length];
                return (
                  <div
                    key={operario.id}
                    className={`${styles.operarioItem} ${
                      selectedOperarios.includes(operario.id)
                        ? styles.selected
                        : ""
                    }`}
                  >
                    <Checkbox
                      checked={selectedOperarios.includes(operario.id)}
                      onChange={() => handleOperarioToggle(operario.id)}
                      color={color}
                      style={{ marginRight: "10px" }}
                  />

                  <span
                    style={{
                      color: "var(--black)",
                      fontFamily: "var(--font-roboto)",
                      cursor: "pointer",
                    }}
                    onClick={() => handleOperarioToggle(operario.id)}
                  >
                    {operario.nombre}
                  </span>

      
                </div>
              );
            })}
            </div>
          </div>
        )}          </>
        ) : (
          // Vista antigua con sidebar fijo - Solo para no-OPERARIOS
          !isOperario && (
            <div className={styles.sidebar}>
              <h2 className={styles.sidebarTitle}>Operarios</h2>
              <Input
                placeholder="Buscar operario..."
                value={searchOperario}
                onChange={(e) => setSearchOperario(e.target.value)}
                style={{ marginBottom: "15px" }}
              />
              <div className={styles.operariosList}>
                {filteredOperarios.map((operario, index) => {
                  const colors = [
                    "#4F46E5",
                    "#10B981",
                    "#F97316",
                    "#8B5CF6",
                    "#EC4899",
                    "#06B6D4",
                    "#84CC16",
                    "#EF4444",
                    "#F59E0B",
                    "#8B5A2B",
                  ];
                  const color = colors[index % colors.length];
                  return (
                    <div key={operario.id} className={styles.operarioItem}>
                      <Checkbox
                        checked={selectedOperarios.includes(operario.id)}
                        onCheckedChange={() => handleOperarioToggle(operario.id)}
                        color={color}
                        style={{ marginRight: "10px" }}
                      />

                      <span
                        style={{
                          color: "var(--black)",
                          fontFamily: "var(--font-roboto)",
                          cursor: "pointer",
                        }}
                        onClick={() => handleOperarioToggle(operario.id)}
                      >
                        {operario.nombre}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )
        )}
        <div className={styles.calendarMain}>
          {/* Barra de información de ausencias - Solo visible para no-OPERARIOS */}
          {!isOperario && operariosAusentes.length > 0 && (
            <div className={styles.ausenciasInfoBar} onClick={() => setShowAusenciasInfoModal(true)}>
              <div className={styles.ausenciasInfoContent}>
                <MyIcons name="alert-circle" width={20} height={20} color="#DC2626" />
                <span className={styles.ausenciasInfoText}>
                  <strong>{operariosAusentes.length}</strong> {operariosAusentes.length === 1 ? 'operario ausente' : 'operarios ausentes'} en este período
                </span>
              </div>
              <MyIcons name="chevron-right" width={20} height={20} color="#666" />
            </div>
          )}
          
          <Calendar
            localizer={localizer}
            events={filteredCitas}
            startAccessor="start"
            endAccessor="end"
            style={{ height: "100%" }}
            defaultView="week"
            view={calendarView}
            onView={(v) => setCalendarView(v)}
            views={["week", "day"]}
            min={new Date(0, 0, 0, 7, 0, 0)}
            max={new Date(0, 0, 0, 20, 0, 0)}
            date={currentDate}
            onNavigate={handleNavigate}
            onSelectEvent={handleSelectEvent}
            step={30}
            timeslots={2}
            showMultiDayTimes={true}
            components={{
              event: EventComponent,
              toolbar: CustomToolbar,
              noEventsInRange: NoEventsOverlay,
            }}
            messages={{
              week: "Semana",
              day: "Día",
              today: "Hoy",
              previous: "Anterior",
              next: "Siguiente",
              allDay: "Todo el día",
              date: "Fecha",
              time: "Hora",
              event: "Evento",
            }}
            eventPropGetter={(event) => {
              // Estilo especial para ausencias
              if (event.isAusencia) {
                return {
                  style: {
                    backgroundColor: "#DC2626",
                    borderRadius: "3px",
                    border: "2px solid #991B1B",
                    color: "white",
                    fontSize: "11px",
                    padding: "2px 4px",
                    opacity: 0.9,
                    fontWeight: "bold",
                  },
                };
              }
              
              const operarioIndex = operarios.findIndex(
                (op) => op.id === event.operarioId
              );
              const colors = [
                "#4F46E5",
                "#10B981",
                "#F97316",
                "#8B5CF6",
                "#EC4899",
                "#06B6D4",
                "#84CC16",
                "#EF4444",
                "#F59E0B",
                "#8B5A2B",
              ];
              const backgroundColor = colors[operarioIndex % colors.length];

              return {
                style: {
                  backgroundColor,
                  borderRadius: "3px",
                  border: "1px solid white",
                  color: "white",
                  fontSize: "11px",
                  padding: "2px 4px",
                },
              };
            }}
          />
        </div>
      </div>
      
      {/* Modal de detalles de cita - Solo para operarios */}
      {isOperario && selectedCita && (
        <AppointmentDetailModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          citaId={selectedCita.citaId || selectedCita.id}
          onUpdate={handleModalUpdate}
        />
      )}
      
      {/* Modal de ausencias - Solo para no-operarios */}
      {!isOperario && (
        <AbsencesModal
          isOpen={isAbsencesModalOpen}
          onClose={() => {
            setIsAbsencesModalOpen(false);
            // Recargar ausencias después de cerrar el modal
            const fetchAusencias = async () => {
              try {
                let fromDate, toDate;
                if (calendarView === 'week') {
                  fromDate = moment(currentDate).startOf('week').format('YYYY-MM-DD');
                  toDate = moment(currentDate).endOf('week').format('YYYY-MM-DD');
                } else if (calendarView === 'day') {
                  fromDate = moment(currentDate).startOf('day').format('YYYY-MM-DD');
                  toDate = moment(currentDate).endOf('day').format('YYYY-MM-DD');
                } else {
                  fromDate = moment(currentDate).startOf('month').format('YYYY-MM-DD');
                  toDate = moment(currentDate).endOf('month').format('YYYY-MM-DD');
                }
                const ausenciasData = await getAusenciasByFechas(fromDate, toDate);
                setAusencias(ausenciasData || []);
              } catch (error) {
                console.error("Error reloading ausencias:", error);
              }
            };
            fetchAusencias();
          }}
        />
      )}
      
      {/* Modal de información de ausencias del rango actual */}
      {showAusenciasInfoModal && (
        <div className={styles.modalOverlay} onClick={() => setShowAusenciasInfoModal(false)}>
          <div className={styles.ausenciasInfoModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>
                <MyIcons name="alert-circle" width={24} height={24} color="#DC2626" />
                Operarios Ausentes
              </h2>
              <button 
                className={styles.closeButton}
                onClick={() => setShowAusenciasInfoModal(false)}
              >
                <MyIcons name="close" width={24} height={24} />
              </button>
            </div>
            <div className={styles.modalBody}>
              {operariosAusentes.map(op => (
                <div key={op.id} className={styles.operarioAusenteCard}>
                  <h3 className={styles.operarioAusenteNombre}>{op.nombre}</h3>
                  <div className={styles.ausenciasDelOperario}>
                    {op.ausencias.map(ausencia => {
                      const inicio = moment(ausencia.fechaInicio);
                      const fin = moment(ausencia.fechaFin);
                      const mismaFecha = inicio.isSame(fin, 'day');
                      
                      return (
                        <div key={ausencia.id} className={styles.ausenciaItem}>
                          <div className={styles.ausenciaFechasDestacado}>
                            {mismaFecha ? (
                              <div className={styles.fechaUnica}>
                                <span className={styles.diaSemana}>{inicio.format('dddd').toUpperCase()}</span>
                                <span className={styles.fechaNumero}>{inicio.format('DD/MM/YYYY')}</span>
                              </div>
                            ) : (
                              <div className={styles.fechaRango}>
                                <div className={styles.fechaInicio}>
                                  <span className={styles.labelDesde}>DESDE</span>
                                  <span className={styles.diaSemana}>{inicio.format('dddd').toUpperCase()}</span>
                                  <span className={styles.fechaNumero}>{inicio.format('DD/MM/YYYY')}</span>
                                </div>
                                <div className={styles.flechaRango}>→</div>
                                <div className={styles.fechaFin}>
                                  <span className={styles.labelHasta}>HASTA</span>
                                  <span className={styles.diaSemana}>{fin.format('dddd').toUpperCase()}</span>
                                  <span className={styles.fechaNumero}>{fin.format('DD/MM/YYYY')}</span>
                                </div>
                              </div>
                            )}
                          </div>
                          {ausencia.motivo && (
                            <div className={styles.ausenciaMotivo}>
                              <strong>Motivo:</strong> {ausencia.motivo}
                            </div>
                          )}
                          {ausencia.observaciones && (
                            <div className={styles.ausenciaObservaciones}>
                              {ausencia.observaciones}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarPage;
